/* Shared helpers for the Pay group pages (payroll, requests, lifecycle, settings…). */
import { useMemo } from "react";
import { useAllEmployees } from "../../../data/hooks";
import { useSession } from "../../../app/session";
import { useI18n } from "../../../i18n";
import { Chip } from "../../../ui";
import type { Employee, Role } from "../../../data/types";

export type PayPerm = "payroll.ops" | "payroll.approve" | "settings.write" | "requests.decide" | "hr.modules" | "letters.issue";
const MATRIX: Record<PayPerm, Role[]> = {
  "payroll.ops": ["owner", "hr", "accountant"],
  "payroll.approve": ["owner", "hr"],
  "settings.write": ["owner", "hr"],
  "requests.decide": ["owner", "hr", "accountant"],
  "hr.modules": ["owner", "hr"],
  "letters.issue": ["owner", "hr"],
};
/** Role of the signed-in user + permission check (mirrors API.md RBAC). */
export function useRole() {
  const { role, user } = useSession();
  return { role, user, ready: role != null, can: (p: PayPerm) => !!role && MATRIX[p].includes(role) };
}

/** All employees indexed by id (for names in tables). */
export function useEmpMap() {
  const q = useAllEmployees();
  const map = useMemo(() => new Map<number, Employee>((q.data ?? []).map((e) => [e.id, e])), [q.data]);
  return { ...q, map, list: q.data ?? [] };
}

/** Placeholder person used when an employee id is not (yet) resolved. */
export const unknownEmp = (id: number): Pick<Employee, "nameEn" | "nameAr" | "code" | "title"> => ({ nameEn: "#" + id, nameAr: "#" + id, code: "", title: "" });

const EXTRA_AR: Record<string, string> = {
  Reimbursed: "تم الصرف", Completed: "مكتمل", "In stock": "في المخزون", Assigned: "مسلّم", "Notice period": "فترة الإشعار",
  Clearance: "إخلاء الطرف", Open: "مفتوحة", "Objection window": "مهلة الاعتراض", Hired: "تم التعيين", Applied: "تقدم",
  Screening: "الفرز", Interview: "المقابلة", Offer: "العرض",
};
const EXTRA_TONE: Record<string, string> = { Reimbursed: "ok", Completed: "ok", "In stock": "info", Assigned: "ok", "Notice period": "warn", Clearance: "warn", Open: "info", "Objection window": "warn" };

/** Status chip with Arabic labels for statuses the shared dictionary lacks. */
export function StatusChip({ status, testId }: { status: string; testId?: string }) {
  const { isAr, t } = useI18n();
  const label = isAr ? EXTRA_AR[status] ?? t(status) : status;
  return <Chip status={status} tone={EXTRA_TONE[status]} testId={testId}>{label}</Chip>;
}

/** Parse a numeric text input: "" → NaN (so validation flags it). */
export const numOf = (s: string): number => (s.trim() === "" ? NaN : Number(s));
